// Boucles de l'onglet Sons (musiques, ambiances) : un fichier lu par un élément <audio> en boucle, passé dans Web Audio
// pour régler son volume en fondu (iOS ignore le volume des éléments <audio>).
import type { Sound } from '../../shared/sounds.ts';
import type { AudioChannel, SoundBoard } from './sound.ts';

/** Fondu enchaîné d'un son à l'autre (secondes). */
export const CROSSFADE = 1.5;
/** Sons quittés gardés en pause, pour reprendre où ils en étaient (la musique de la carte après un combat). */
const KEPT = 2;

class Loop {
  /** Le son lu (son volume peut changer dans l'administration, pas son fichier). */
  sound: Sound;
  private readonly element: HTMLAudioElement;
  private readonly source: MediaElementAudioSourceNode;
  private readonly gain: GainNode;
  private readonly ctx: AudioContext;
  /** Mise en pause prévue à la fin d'un fondu de sortie. */
  private pauseTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(ctx: AudioContext, bus: AudioNode, sound: Sound) {
    this.ctx = ctx;
    this.sound = sound;
    this.element = new Audio(sound.url);
    this.element.loop = true;
    this.element.preload = 'auto';
    this.source = ctx.createMediaElementSource(this.element);
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    this.source.connect(this.gain).connect(bus);
  }

  /** Monte (ou descend) jusqu'à `level` en `seconds`, en lisant le fichier. */
  fadeIn(level: number, seconds: number): void {
    clearTimeout(this.pauseTimer);
    this.ramp(level * this.sound.volume, seconds);
    // Refusé tant que le joueur n'a rien touché : `resume` relance la lecture au déblocage du son.
    if (this.element.paused) void this.element.play().catch(() => undefined);
  }

  /** Descend jusqu'au silence en `seconds`, puis se met en pause (la lecture reprendra au même endroit). */
  fadeOut(seconds: number): void {
    this.ramp(0, seconds);
    clearTimeout(this.pauseTimer);
    this.pauseTimer = setTimeout(() => this.element.pause(), seconds * 1000 + 50);
  }

  /** Relance la lecture si elle a été refusée (avant le premier geste du joueur). */
  resume(): void {
    if (this.element.paused) void this.element.play().catch(() => undefined);
  }

  dispose(): void {
    clearTimeout(this.pauseTimer);
    this.element.pause();
    this.element.removeAttribute('src');
    this.element.load();
    this.source.disconnect();
    this.gain.disconnect();
  }

  private ramp(value: number, seconds: number): void {
    const { gain } = this.gain;
    const now = this.ctx.currentTime;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(gain.value, now);
    gain.linearRampToValueAtTime(value, now + Math.max(seconds, 0.01));
  }
}

/**
 * Une piste de boucles sur un canal (la musique, les bruits de fond, la météo) : un son à la fois, remplacé en fondu
 * enchaîné. Les derniers sons quittés restent en pause, et reprennent où ils en étaient s'ils reviennent.
 */
export class LoopDeck {
  private readonly sounds: SoundBoard;
  private readonly channel: AudioChannel;
  private current: Loop | null = null;
  private readonly kept: Loop[] = [];

  constructor(sounds: SoundBoard, channel: AudioChannel) {
    this.sounds = sounds;
    this.channel = channel;
    sounds.onUnlock(() => this.current?.resume());
  }

  /** Joue `sound` à `level` (0 à 1, multiplié au volume propre du son) ; null : silence. Le même son ne fait que changer de volume. */
  set(sound: Sound | null, level = 1, seconds = CROSSFADE): void {
    const same = (loop: Loop) => sound !== null && loop.sound.id === sound.id && loop.sound.url === sound.url;
    if (this.current && same(this.current)) {
      this.current.sound = sound!;
      this.current.fadeIn(level, seconds);
      return;
    }
    if (this.current) this.keep(this.current, seconds);
    this.current = null;
    if (!sound || level <= 0) return;
    const ctx = this.sounds.audio();
    const index = this.kept.findIndex(same);
    this.current = index >= 0 ? this.kept.splice(index, 1)[0]! : new Loop(ctx, this.sounds.bus(this.channel), sound);
    this.current.sound = sound;
    this.current.fadeIn(level, seconds);
  }

  /** Coupe tout, sans rien garder (déconnexion). */
  clear(): void {
    this.current?.dispose();
    this.current = null;
    for (const loop of this.kept.splice(0)) loop.dispose();
  }

  private keep(loop: Loop, seconds: number): void {
    loop.fadeOut(seconds);
    this.kept.push(loop);
    while (this.kept.length > KEPT) this.kept.shift()!.dispose();
  }
}
